/**
 * SidebarRecentsController
 *
 * Owns the sidebar's single collapsible Recents section — replacing the old
 * dead "Recent Chats" scaffolding (see RecentChatsService, deleted).
 *
 * One list, two blocks. Starred items sort to the top (by `starredAt` desc,
 * regardless of type, archived state, or practice status), marked by a gold
 * star badge on the row icon; the remainder follows by recency (session notes
 * + received partner notes + ready guides + in-progress (active) practices,
 * excluding archived and sent notes; threads never reach this block). An item
 * is eligible when it is starred OR recents-eligible, so starring remains the
 * way to keep an archived note or a completed practice reachable here. A
 * hairline divider separates the blocks; the starred block soft-caps at
 * STARRED_CAP behind a reversible expander.
 *
 * Reuses the SAME federated data sources `/search` (SessionsSection) already
 * uses (`searchSources` for loading/normalizing). Open threads and practices
 * are the two exceptions — neither has a presence on /search. Practices are
 * loaded via the shared `PRACTICE_SOURCE` (searchSources.js — also consumed
 * by DashboardManager's Recent/Starred views); open threads have no second
 * consumer, so `THREAD_SOURCE` stays defined locally below rather than
 * joining the shared `searchSources` list. The row menu (`_rowOptionsHtml`)
 * and every mutation (star/archive/delete/mark-viewed/flag/discuss) are
 * built by the shared `contentRowActions.js` module — `buildContentRowMenuItems`
 * + the `run*`/`discussContentItem`/`confirmDeleteContentItem` functions —
 * which `DashboardManager`'s Recent/Starred library rows reuse for
 * sidebar-parity kebabs, so the two surfaces can never drift apart on menu
 * contents or mutation endpoints. Experiment rows mirror `/experiments`'
 * lifecycle menu: Report back + Archive for active rows, Restore for archived
 * rows, and Delete for every status.
 * Frontend-only — no new backend endpoints beyond the practices `/view`
 * route becoming reversible (mirroring the pattern already established for
 * sessions/memos/guides).
 *
 * Cross-surface sync (contentSync bridge): this controller both broadcasts and
 * listens on the universal `content:starred` / `content:archived` /
 * `content:deleted` events (see `_onContentStarred`/`_onContentArchived`/
 * `_onContentDeleted`). Every star/archive/delete — from this controller's own
 * rows, `/search`'s row kebab or select-mode, `/notes`, or the Open Threads tab
 * on `/discovery` — now live-syncs across all four surfaces (session + partner
 * note + open thread). Each surface applies its own row change first, then
 * emits, so the emitter's own listener self-skips; this controller re-renders
 * on any inbound event for an item it holds. Items beyond the loaded set
 * (`limit: 100`) aren't found → skipped (acceptable for a recents view).
 *
 * `content:viewed` is a fourth bridge event. Unread presentation is derived
 * from the item itself: the icon dot and the Mark as read/unread action. Note
 * the dot yields to the star badge on a starred row (see `_rowHtml`), while
 * the kebab action stays available either way.
 * It is bidirectional: native surfaces and sidebar actions emit
 * the same event, while this controller, native section caches, and
 * BadgeService subscribe to keep every viewed-state renderer live.
 *
 * **Sessions don't use `content:viewed`/the automatic dot at all** (2026-07-13
 * revision — see `project-docs/plans/search-notes-dots-and-practices-sidebar.md`):
 * a chat is never "new" to the person who was in it, so sessions get a
 * manual unread toggle instead, backed by a dedicated `SessionNote.flaggedAt`
 * field (NOT a repurposed `viewedAt` — that already auto-stamps on native open
 * — or `starredAt`, already overloaded with the episodic-retrieval value
 * signal). The UI deliberately uses the same Mark as read/unread wording and
 * eye icons as other rows, while `content:flagged` remains the session-only
 * persistence bridge. Note/guide/practice rows keep their automatic
 * `viewed`-driven state.
 *
 * @module services/SidebarRecentsController
 */

import page from '../vendor/page.js';
import { API } from '../core/api.js';
import { eventBus, escapeHtml } from '../core/utils.js';
import {
  CONTENT_STARRED,
  CONTENT_ARCHIVED,
  CONTENT_DELETED,
  CONTENT_VIEWED,
  CONTENT_FLAGGED,
  reduceNormalizedItems
} from '../core/contentSync.js';
import {
  searchSources,
  stampContinuationTitles,
  iconIdForItem,
  iconSizeForItem,
  PRACTICE_SOURCE,
  isRecentsEligible
} from '../components/searchSources.js';
import { openContentItem } from '../components/contentNavigation.js';
import {
  renderCollapsibleSection,
  setCollapsibleSectionExpanded,
  loadCollapsedState,
  saveCollapsedState
} from '../components/CollapsibleSection.js';
import { renderRowKebab } from '../components/optionsMenu.js';
import { Tooltip } from '../utils/Tooltip.js';
import {
  STAR_ICON,
  getRowUnreadState,
  buildContentRowMenuItems,
  confirmDeleteContentItem,
  discussContentItem,
  runStar,
  runArchive,
  runMarkViewed,
  runToggleFlag,
  runDelete
} from '../components/contentRowActions.js';

/** Hover delay (ms) before a truncated row title's marquee scroll starts —
 * long enough that running the cursor down the list doesn't kick one off per
 * row, short enough to still feel responsive on a deliberate hover. */
const ROW_TITLE_MARQUEE_DELAY_MS = 250;

/** Marquee scroll speed (px/second) the reveal distance is divided by to get
 * a duration — kept unclamped so every title scrolls at the SAME speed
 * (a longer reveal just takes proportionally longer), rather than a fixed
 * duration that would make long titles rush and short ones crawl. */
const MARQUEE_PX_PER_SEC = 55;

/** Header-level "See all" glyph — an arrow pointing out of a corner, sized
 * down further inside the button (see .cs-section__action svg in
 * collapsible-section.css). Icon-only; the label surfaces as a tooltip. */
const SEE_ALL_ICON = `<svg width="16" height="16" viewBox="0 0 10 10" fill="none" aria-hidden="true">
      <path d="M3 7L7 3M7 3H4M7 3V6" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"></path>
    </svg>`;

/**
 * Clamp a long string to a single-line-ish length — mirrors
 * `DiscoverySection._clampToOneLine` (duplicated rather than imported; that
 * method lives on the DiscoverySection instance, not as a standalone export).
 * @param {string} text
 * @returns {string}
 */
function clampThreadTitle(text) {
  const clean = String(text || '').trim();
  if (clean.length <= 90) return clean;
  return `${clean.slice(0, 89).trimEnd()}…`;
}

/**
 * Normalize a raw OpenThread into a SearchItem-shaped row for the starred
 * block. `archived` tracks `dismissedAt` only (the user-toggle field
 * `archiveOpenThreads` flips) — the thread's own system `archivedAt` (quiet
 * expiry) rides along unmodified on `payload` for `_navigateToItem` to
 * resolve the right /discovery tab.
 * @param {Object} thread - OpenThread row from `API.getOpenThreads`
 * @returns {Object} SearchItem
 */
function normalizeOpenThread(thread) {
  return {
    type: 'thread',
    id: thread.id,
    title: (thread.headline && thread.headline.trim()) || clampThreadTitle(thread.question),
    date: thread.createdAt,
    starred: Boolean(thread.starredAt),
    starredAt: thread.starredAt || null,
    archived: Boolean(thread.dismissedAt),
    archivedAt: thread.dismissedAt || null,
    payload: thread
  };
}

/**
 * Starred-open-threads source — sidebar-only, NOT part of the shared
 * `searchSources` list (open threads have no presence on /search). Loads the
 * full unfiltered set (`status: 'all'`) since a starred thread can be in any
 * lifecycle state, and normalizes only the starred ones — threads reach the
 * sidebar exclusively through the starred block.
 */
const THREAD_SOURCE = {
  type: 'thread',
  label: 'threads',
  async load() {
    const data = await API.getOpenThreads({ status: 'all', limit: 100 });
    if (!Array.isArray(data)) {
      throw new Error('[SidebarRecentsController] threads: unexpected payload shape');
    }
    return data;
  },
  normalize(threads) {
    return threads.filter(t => t.starredAt).map(normalizeOpenThread);
  }
};

export class SidebarRecentsController {
  /** The one section's key — stamped as `data-cs-section` by
   * `renderCollapsibleSection` and used as the per-user collapse-state
   * localStorage key. Nothing in CSS is keyed on it (the row rules are
   * key-agnostic), so it is free to be a plain identifier. */
  static SECTION_KEY = 'recents';

  /** Soft cap on the STARRED BLOCK at the top of the merged list (against the
   * loaded set, not a true backend total — the `session` source loads
   * `limit: 100`). Past this cap the block collapses behind a reversible
   * `+N more starred` / `Show fewer` expander (see `_starredExpanded`) — never
   * silent truncation. The cap exists so an enthusiastic starrer can't push
   * the recency block off the viewport entirely.
   *
   * Collapsing needs a margin of TWO, not one: the expander is itself a row, so
   * hiding a single item saves nothing (see `_sectionHtml`). Exactly CAP + 1
   * starred items therefore render in full. */
  static STARRED_CAP = 5;

  /** Loaded-count cap for the recency block below the divider (same loaded-set
   * caveat). Spans 4 content types (sessions + received notes + guides + active
   * practices), so other recent content can push fewer sessions into the
   * visible cap. Past this cap, the footer "See all recents" navigates to
   * /search rather than expanding in place. */
  static RECENTS_CAP = 25;

  /**
   * @param {HTMLElement} container - Mount element (`#sidebar-recents-sections`)
   * @param {Object} [opts]
   * @param {string} [opts.userId] - Current user's id — namespaces the
   *   per-user collapse-state localStorage keys (see CollapsibleSection.js)
   */
  constructor(container, { userId } = {}) {
    this.container = container;
    this.userId = userId;

    /** Merged SearchItems from all sources (session/note/guide/thread/practice). */
    this.items = [];

    /** Source types ('session'|'note'|'guide'|'thread'|'practice') whose `load()` rejected on
     * the last `_loadItems()` — kept distinct from "loaded successfully with
     * zero items" so a load failure is never silently indistinguishable from
     * an empty section (no-silent-failures standard). */
    this._sourceFailed = new Set();

    /** Collapse state for the one section, keyed by SECTION_KEY. `_sectionHtml`
     * reads `this.expanded[SECTION_KEY]`, so this key must match the key handed
     * to `renderCollapsibleSection` — a mismatch reads `undefined` and renders
     * the whole section collapsed on every load. */
    this.expanded = {
      [SidebarRecentsController.SECTION_KEY]:
        loadCollapsedState(SidebarRecentsController.SECTION_KEY, userId, true)
    };

    /** The starred block's expander was clicked → render every starred item
     * instead of capping at STARRED_CAP. In-memory only (not persisted), and
     * reversible via the same control ("Show fewer") — a one-way latch would
     * permanently push the recency block below the fold for a heavy starrer,
     * who can no longer collapse the starred block on its own. */
    this._starredExpanded = false;

    /** "type:id" of the row whose kebab menu is open (see `_itemKey`). Each
     * item renders exactly one row now, so the key needs no section qualifier. */
    this._openMenuKey = null;

    /** The open menu's `.options-dropdown__menu` element + its original
     * parent (`.note-row__actions`) — see `_toggleRowMenu`'s body-reparent
     * portal, which escapes `.sidebar__recents`' internal scroll clipping.
     * `_menuRowEl` is the open row itself, needed (alongside `_menuEl`) by
     * `_handleMenuAreaMouseLeave` since the portal breaks the row/menu DOM
     * adjacency a plain `mouseleave` would otherwise rely on. */
    this._menuEl = null;
    this._menuHomeParent = null;
    this._menuRowEl = null;

    this._boundCloseRowMenu = this._closeRowMenu.bind(this);
    this._boundMenuAreaMouseLeave = this._handleMenuAreaMouseLeave.bind(this);
    this._boundHandleClick = this._handleClick.bind(this);
    this._boundHandleContextMenu = this._handleContextMenu.bind(this);

    /** Disables all row actions while one is in flight (star/archive/delete
     * are single-flag-guarded, mirroring SessionsSection._runBulk — a rapid
     * second click before the first call resolves must not double-fire). */
    this._actionInFlight = false;

    this._initialized = false;
  }

  /**
   * Load data, subscribe to live-refresh events, render, and wire the
   * delegated click handler. Idempotent — safe to call more than once.
   *
   * Caller contract (load-bearing): must be invoked only after
   * `window.app.currentUser` is readable, since the `note` source's couple
   * gate reads `window.app?.currentUser?.coupleId` synchronously inside
   * `load()`. See app.js's instantiation-timing comment.
   * @returns {Promise<void>}
   */
  async initialize() {
    if (!this.container || this._initialized) return;
    this._initialized = true;
    this._subscribeEvents();
    this.container.addEventListener('click', this._boundHandleClick);
    this.container.addEventListener('contextmenu', this._boundHandleContextMenu);
    await this._refresh();
  }

  /**
   * Subscribe to the events that should reload the federated stream
   * (session lifecycle + couple-unlink), plus the one real cross-page star
   * bridge event. Called once from `initialize()`.
   */
  _subscribeEvents() {
    const refresh = () => { this._refresh(); };
    eventBus.on('session:started', refresh);
    eventBus.on('session:deleted', refresh);
    eventBus.on('session:ended', refresh);
    // A session made private mid-life must drop out of the list immediately
    // rather than lingering until the next lifecycle event.
    eventBus.on('session:privacyModeChanged', refresh);

    // Guest→account conversion flips every source above from unreachable to
    // loadable. Nothing else in this set fires on the exit-and-convert route
    // (which lands on the dashboard without starting a session), so without
    // this the new account's sidebar stays empty until a page load.
    eventBus.on('auth:account-created', refresh);

    // Sidebar.js's own partner:unlinked handler only hides the static Notes
    // nav item — it has no awareness of this controller's in-memory list, so
    // this needs its own explicit subscription to drop starred partner notes.
    eventBus.on('partner:unlinked', () => this._onPartnerUnlinked());

    // Universal cross-surface sync bridge (contentSync) — star/archive/delete
    // from any surface live-sync into the sidebar. See module doc.
    eventBus.on(CONTENT_STARRED, (data) => this._onContentStarred(data));
    eventBus.on(CONTENT_ARCHIVED, (data) => this._onContentArchived(data));
    eventBus.on(CONTENT_DELETED, (data) => this._onContentDeleted(data));
    // Bidirectional viewed-state bridge — sidebar and native surfaces both emit
    // and subscribe through their own local caches.
    eventBus.on(CONTENT_VIEWED, (data) => this._onContentViewed(data));
    // Session-only manual flag toggle — same cross-surface bridge shape.
    eventBus.on(CONTENT_FLAGGED, (data) => this._onContentFlagged(data));
  }

  /**
   * Reload the federated stream and re-render.
   * @returns {Promise<void>}
   */
  async _refresh() {
    await this._loadItems();
    this._render();
  }

  /**
   * Load every `searchSources` adapter in parallel (same sources `/search`
   * uses) via `Promise.allSettled` — a rejected source is recorded in
   * `_sourceFailed` (and logged) rather than silently treated as "zero
   * items" for that section. Items with null/invalid dates are dropped
   * (an invalid date sorts unpredictably against `starredAt`/`date`).
   * @returns {Promise<void>}
   */
  async _loadItems() {
    // Every Recents source is an auth-only route (session-notes, guides,
    // open-threads, practices), so for a guest they can only ever ALL fail.
    // The failure notice below is deliberately loud, which is right for an
    // account holder and wrong here: a guest would see a permanent "Couldn't
    // load some items" for content they cannot have. Not attempting the load
    // is what makes the section correctly absent instead of broken-looking —
    // `_sectionHtml` returns '' for an empty, unfailed set, which hides the
    // whole section including its header.
    if (window.app?.isGuest === true) {
      this._sourceFailed = new Set();
      this.items = [];
      return;
    }

    const ctx = { stampContinuationTitles };
    const sources = [...searchSources, THREAD_SOURCE, PRACTICE_SOURCE];
    const results = await Promise.allSettled(sources.map(source => source.load(ctx)));

    const items = [];
    const failed = new Set();
    results.forEach((result, i) => {
      const source = sources[i];
      if (result.status === 'fulfilled') {
        items.push(...source.normalize(result.value));
      } else {
        console.error(`[SidebarRecentsController] Failed to load ${source.label}:`, result.reason);
        failed.add(source.type);
      }
    });

    this._sourceFailed = failed;
    this.items = items.filter(item =>
      item.date && !Number.isNaN(new Date(item.date).getTime())
    );
  }

  /**
   * Split the loaded set into the merged section's two blocks.
   *
   * `starred` is unconditional on type, archived state, and practice status —
   * starring is how an archived note, a completed practice, or an open thread
   * stays reachable in the sidebar at all. `rest` is the recency block, which
   * additionally drops anything already shown above so no item renders twice
   * (that duplication is precisely what merging the old two sections removed).
   * @returns {{starred: Object[], rest: Object[]}} Ordered blocks
   */
  _computeItems() {
    const starred = this.items
      .filter(item => item.starred)
      .sort((a, b) => new Date(b.starredAt) - new Date(a.starredAt));

    const seen = new Set(starred.map(item => SidebarRecentsController._itemKey(item)));
    const rest = this.items
      .filter(item =>
        !seen.has(SidebarRecentsController._itemKey(item)) && isRecentsEligible(item)
      )
      .sort((a, b) => new Date(b.date) - new Date(a.date));

    return { starred, rest };
  }

  /**
   * Render the merged Recents section into the mount container.
   */
  _render() {
    if (!this.container) return;
    this._closeRowMenu();

    this.container.innerHTML = this._sectionHtml();

    // Full-title reveal on hover — slow left-scroll of the truncated title's
    // inner span (marquee), replacing the native/tooltip title reveal. Re-run
    // on every render since `innerHTML` above discards the previous rows
    // (and their listeners) wholesale.
    this._bindTitleMarquee();

    // Header-level "See all" icon button — no delay, matching the kebab-style
    // icon buttons the tooltip is standing in for.
    Tooltip.setupAll(this.container, '.cs-section__action[data-tooltip]', {
      position: 'below'
    });
  }

  /**
   * Wire the row-title marquee: on hover (after `ROW_TITLE_MARQUEE_DELAY_MS`),
   * a truncated title's inner `.sidebar-recent-row__title-text` slides left
   * just far enough to bring its clipped end into view, then slides back on
   * mouse-leave. Mirrors `Tooltip.setup`'s per-element mouseenter/mouseleave
   * + closure-scoped timer shape, since it's re-bound on every `_render()`
   * for the same reason (the previous rows and their listeners are gone).
   * Rows that don't actually overflow are silently no-ops.
   */
  _bindTitleMarquee() {
    this.container.querySelectorAll('.sidebar-recent-row__title').forEach(titleEl => {
      const textEl = titleEl.querySelector('.sidebar-recent-row__title-text');
      if (!textEl) return;

      let hoverTimer = null;

      titleEl.addEventListener('mouseenter', () => {
        hoverTimer = setTimeout(() => {
          const distance = textEl.scrollWidth - titleEl.clientWidth;
          if (distance <= 0) return;
          const duration = (distance / MARQUEE_PX_PER_SEC) * 1000;
          textEl.style.transitionDuration = `${duration}ms`;
          textEl.style.transform = `translateX(-${distance}px)`;
        }, ROW_TITLE_MARQUEE_DELAY_MS);
      });

      titleEl.addEventListener('mouseleave', () => {
        clearTimeout(hoverTimer);
        textEl.style.transitionDuration = '';
        textEl.style.transform = '';
      });
    });
  }

  /**
   * Render the merged Recents section, or '' to hide it entirely. Hidden only
   * when genuinely empty on a SUCCESSFUL load — a load failure renders the
   * section with a distinct notice instead, so "you have nothing here" and
   * "this failed to load" never look identical.
   *
   * Body order: failure notice, starred rows, the starred block's expander,
   * the hairline divider, the recency rows, the footer "See all recents". The
   * expander belongs to the starred block, so it sits ABOVE the divider.
   * @returns {string} HTML string ('' hides the section)
   */
  _sectionHtml() {
    const { starred, rest } = this._computeItems();
    // Any source failing is the whole signal — `_sourceFailed` can only ever
    // hold the five sources `_loadItems` loads, so there is nothing to
    // enumerate (the previous per-type lists went stale twice as sources were
    // added).
    const failed = this._sourceFailed.size > 0;
    if (starred.length === 0 && rest.length === 0 && !failed) return '';

    // Rendered ABOVE the rows and gated only on `failed`, not on emptiness: in
    // a merged list an empty union means a brand-new account, so an
    // empty-only gate would hide nearly every real failure behind a
    // normal-looking list with items silently missing (no-silent-failures
    // standard). Deliberately source-neutral — a failed guides load must not
    // claim "Couldn't load recents."
    const noticeHtml = failed
      ? '<div class="sidebar-recents__notice">Couldn\'t load some items.</div>'
      : '';

    // Collapsing is only worth it when it hides at least TWO rows: the expander
    // occupies a full row itself, so capping at 5 to hide a 6th costs exactly
    // the row it saves — a control that buys the user nothing but a click. One
    // over the cap therefore renders in full, with no expander in either state.
    const collapsible = starred.length > SidebarRecentsController.STARRED_CAP + 1;
    const visibleStarred = (collapsible && !this._starredExpanded)
      ? starred.slice(0, SidebarRecentsController.STARRED_CAP)
      : starred;
    const starredRows = visibleStarred.map(item => this._rowHtml(item)).join('');

    // Reversible by design — "Show fewer" is the way back. See `_starredExpanded`.
    // Gating on `collapsible` (not on the hidden count) also means unstarring
    // back down to the cap retires the control rather than stranding an inert
    // "Show fewer".
    const hiddenStarred = starred.length - visibleStarred.length;
    const expanderHtml = collapsible
      ? `<button type="button" class="card sidebar-recent-row sidebar-recent-row--show-all sidebar__item" data-show-all-expand="starred">${this._starredExpanded ? 'Show fewer' : `+${hiddenStarred} more starred`}</button>`
      : '';

    const restRows = rest
      .slice(0, SidebarRecentsController.RECENTS_CAP)
      .map(item => this._rowHtml(item))
      .join('');

    // Marks the starred/recency boundary, which is otherwise invisible once
    // the two section headers are gone. Only meaningful between two non-empty
    // blocks.
    const dividerHtml = (visibleStarred.length > 0 && rest.length > 0)
      ? '<div class="sidebar-recents__divider" aria-hidden="true"></div>'
      : '';

    // Always rendered (not gated on overflow): the loaded set is a page, not
    // the true backend total, so more content may exist beyond it either way.
    const seeAllLabel = 'See all recents';
    const footerHtml = `<button type="button" class="card sidebar-recent-row sidebar-recent-row--show-all sidebar__item" data-show-all-href="/search">${seeAllLabel}</button>`;

    // Header-level "See all" — hover-revealed regardless of item count (see
    // collapsible-section.css). Icon-only (an arrow-out-of-corner glyph); the
    // label surfaces as a hover tooltip + aria-label instead of visible text,
    // wired via Tooltip.setupAll in `_render`. Always an <a> with a real href
    // so it stays keyboard-reachable/middle-click-able despite nesting inside
    // the header toggle button; `_handleClick` intercepts the click and
    // prevents the plain-navigation default in favor of the page.js SPA route.
    const actionHtml = `<a href="/search" class="cs-section__action" data-show-all-href="/search" data-tooltip="${escapeHtml(seeAllLabel)}" aria-label="${escapeHtml(seeAllLabel)}">${SEE_ALL_ICON}</a>`;

    return renderCollapsibleSection({
      key: SidebarRecentsController.SECTION_KEY,
      label: 'Recents',
      expanded: this.expanded[SidebarRecentsController.SECTION_KEY],
      actionHtml,
      bodyHtml: noticeHtml + starredRows + expanderHtml + dividerHtml + restRows + footerHtml
    });
  }

  /**
   * Render one item row. Mirrors SessionsSection.renderItemCard's icon/title/
   * star/kebab structure, trimmed (no date, no select-mode) — `sidebar__item`
   * is included so the existing mobile-overlay auto-close behavior fires.
   * @param {Object} item - SearchItem
   * @returns {string} HTML string
   */
  _rowHtml(item) {
    const menuKey = SidebarRecentsController._itemKey(item);
    const title = escapeHtml(item.title || '');

    // Every row renders its per-type icon — the list mixes all 5 content types,
    // so the "homogeneous, icon is redundant" rationale never applied here.
    // A guide takes the book glyph here even when its path has artwork: this
    // is a typed, monochrome list whose icon's one job is the type, and a
    // guide's title is already its path's name, so the cover page /search and
    // the dashboard rows draw (guideCoverHtml) would only add a coloured
    // smudge at 20px. Same wrapper as every row, so the star and dot siblings
    // position identically.
    const iconSize = iconSizeForItem(item);
    const glyphHtml = `<svg width="${iconSize}" height="${iconSize}"><use href="#icon-${iconIdForItem(item)}"/></svg>`;
    const iconInnerHtml = `<span class="sidebar-recent-row__icon" aria-hidden="true">
          ${glyphHtml}
        </span>`;

    // Star badge and unread dot share the icon's top-right corner, and the
    // STAR WINS: with no separate Starred section, the badge is the only thing
    // marking a row as starred and the only explanation for its position at the
    // top of the list, whereas the dot's state stays legible on /search rows and
    // in this row's own Mark as read/unread kebab item. Two marks on one 20px
    // glyph is noise.
    //
    // Sessions read the dedicated manual `flagged` state; other eligible types
    // read `viewed`. Threads and sent notes intentionally have no unread
    // presentation. Both marks render as SIBLINGS of .sidebar-recent-row__icon,
    // never nested inside it — that class carries opacity: 0.85, which would
    // wash them out with no way for a child to opt back out.
    const starred = Boolean(item.starred);
    const showDot = !starred && Boolean(getRowUnreadState(item)?.unread);
    const starHtml = starred
      ? `<span class="sidebar-recent-row__icon-star" role="img" aria-label="Starred">${STAR_ICON}</span>`
      : '';
    const dotHtml = showDot ? '<span class="sidebar-recent-row__icon-dot" aria-hidden="true"></span>' : '';
    const iconHtml = `<span class="sidebar-recent-row__icon-wrap">${iconInnerHtml}${starHtml}${dotHtml}</span>`;

    return `
      <div class="card sidebar-recent-row sidebar__item"
           data-item-type="${escapeHtml(item.type)}"
           data-item-id="${escapeHtml(String(item.id))}"
           role="button" tabindex="0">
        ${iconHtml}
        <span class="sidebar-recent-row__title"><span class="sidebar-recent-row__title-text">${title}</span></span>
        <span class="sidebar-recent-row__actions">
          ${this._rowOptionsHtml(menuKey, item)}
        </span>
      </div>
    `;
  }

  /**
   * Render the hover-revealed kebab options button + dropdown menu for one
   * row — reuses the shared options-dropdown classes (dropdowns.css) and
   * mirrors SessionsSection.renderRowOptions' markup, keyed by the item key.
   * The row carries no standalone star button — Star/Unstar lives in the menu,
   * and the row's own star badge is a marker, not a control. renderMenuItems floats
   * the discuss action to the top, so the rendered order is Discuss (or Report
   * back for experiments), Star/Unstar, Mark as read/unread, Archive/Restore,
   * Delete (sent notes omit Discuss and read state). Experiment lifecycle items
   * mirror `/experiments`:
   * Archive for active rows, Restore for archived rows, neither for completed
   * rows, and Delete for all statuses.
   * @param {string} menuKey - "type:id" (see `_itemKey`)
   * @param {Object} item - SearchItem
   * @returns {string} HTML string
   */
  _rowOptionsHtml(menuKey, item) {
    return renderRowKebab({
      items: buildContentRowMenuItems(item),
      dataAttrs: { 'data-menu-key': menuKey },
      ariaLabel: item.type === 'practice' ? 'Experiment options' : 'Options'
    });
  }

  /**
   * The elements that can scroll this list, nearest first: the mount itself
   * (the scroller on desktop) and its `.sidebar__scroll` ancestor (the scroller
   * below 1020px). De-duplicated, and absent ancestors are dropped — a test
   * harness mounting a bare container gets just the container back.
   * @param {HTMLElement} container - The controller's mount element
   * @returns {HTMLElement[]}
   */
  static _scrollParents(container) {
    return [...new Set([container, container?.closest?.('.sidebar__scroll')])].filter(Boolean);
  }

  /**
   * Build an item's identity key ("type:id") — the single key format used for
   * both the open-menu handle and `_computeItems`' de-duplication. Ids are
   * UUIDs (sessions/notes/practices/threads) or hyphenated slugs (guides), so
   * none contains the delimiter.
   * @param {Object} item - SearchItem
   * @returns {string}
   */
  static _itemKey(item) {
    return `${item.type}:${item.id}`;
  }

  /**
   * Parse an item key back into its (type, id) pair.
   * @param {string} itemKey - "type:id"
   * @returns {{type: string, id: string}}
   */
  static _parseItemKey(itemKey) {
    const [type, id] = itemKey.split(':');
    return { type, id };
  }

  /**
   * Delegated click handler for the mount container: section-collapse
   * toggle, "Show all" navigation, row-kebab open/menu actions (star |
   * archive | delete), and row-body click → navigate.
   * @param {MouseEvent} event
   */
  _handleClick(event) {
    // Checked BEFORE the toggle branch: the header-level "See all" link
    // (`.cs-section__action`) is a DOM descendant of the `.cs-section__header`
    // toggle button (`[data-cs-toggle]`), so `closest()` from a click on it
    // would otherwise resolve to the toggle first and collapse/expand the
    // section instead of navigating.
    // The starred block's expander. Toggles unconditionally rather than
    // reading the attribute's value — there is exactly one expander, and a
    // value-dependent guard would silently swallow the click if the attribute
    // were ever rendered bare.
    const showAllExpand = event.target.closest('[data-show-all-expand]');
    if (showAllExpand && this.container.contains(showAllExpand)) {
      event.preventDefault();
      this._starredExpanded = !this._starredExpanded;
      this._render();
      return;
    }

    const showAll = event.target.closest('[data-show-all-href]');
    if (showAll && this.container.contains(showAll)) {
      event.preventDefault();
      const href = showAll.getAttribute('data-show-all-href');
      this._closeRowMenu();
      if (href) page(href);
      return;
    }

    const toggle = event.target.closest('[data-cs-toggle]');
    if (toggle && this.container.contains(toggle)) {
      const key = toggle.dataset.csKey;
      if (key) this._toggleSection(key);
      return;
    }

    const actionTarget = event.target.closest('[data-action]');
    // The open menu's items live under `this._menuEl` once portal-reparented
    // to <body> (see `_toggleRowMenu`) — no longer a `this.container`
    // descendant, so the containment check must accept either.
    if (actionTarget && (this.container.contains(actionTarget) || this._menuEl?.contains(actionTarget))) {
      event.stopPropagation();
      const action = actionTarget.getAttribute('data-action');
      const menuKey = actionTarget.getAttribute('data-menu-key');
      const ROW_ACTIONS = {
        'row-star': 'star',
        'row-archive': 'archive',
        'row-restore': 'archive',
        'row-delete': 'delete',
        'row-view': 'view',
        'row-flag': 'flag'
      };
      if (action === 'row-options') {
        this._toggleRowMenu(actionTarget);
      } else if (action === 'row-discuss') {
        this._closeRowMenu();
        if (menuKey) this._discussFromMenuKey(menuKey);
      } else if (ROW_ACTIONS[action]) {
        this._closeRowMenu();
        if (menuKey) this._runRowAction(ROW_ACTIONS[action], menuKey);
      }
      return;
    }

    // The "Show all" button is caught by the [data-show-all-href] branch
    // above (it always matches before falling through here), so any row
    // reaching this point is a real item row.
    const row = event.target.closest('.sidebar-recent-row');
    if (row && this.container.contains(row)) {
      const { itemType, itemId } = row.dataset;
      if (itemType && itemId) this._navigateToItem(itemType, itemId);
    }
  }

  /**
   * Right-click on a row opens that row's options menu in place of the
   * browser's native context menu — same menu the hover-revealed kebab
   * button opens, just reached a second way.
   * @param {MouseEvent} event
   */
  _handleContextMenu(event) {
    const row = event.target.closest('.sidebar-recent-row');
    if (!row || !this.container.contains(row)) return;
    const kebabButton = row.querySelector('[data-action="row-options"]');
    if (!kebabButton) return;
    event.preventDefault();
    this._toggleRowMenu(kebabButton);
  }

  /**
   * Toggle the section's collapse state, persisting it per-user.
   * @param {string} key - The section key (`SECTION_KEY`)
   */
  _toggleSection(key) {
    this.expanded[key] = !this.expanded[key];
    saveCollapsedState(key, this.userId, this.expanded[key]);
    setCollapsibleSectionExpanded(this.container, key, this.expanded[key]);
  }

  /**
   * Resolve a (type, id) pair to its SearchItem and open it on its native
   * detail surface, via the shared {@link openContentItem} (contentNavigation.js
   * carries the full per-type routing rationale — session/note/thread/guide/
   * practice, incl. why each param is required). This wrapper exists only for
   * the find-by-key step, since the sidebar's in-memory `items` cache isn't
   * visible outside the controller.
   * @param {string} type - SearchItem type ('session' | 'note' | 'thread' | 'guide' | 'practice')
   * @param {string} id - SearchItem id
   */
  _navigateToItem(type, id) {
    const item = this.items.find(it => it.type === type && String(it.id) === String(id));
    if (!item) return;
    openContentItem(item);
  }

  /**
   * Resolve a menu key to its item and open a NEW chat session to discuss it
   * via the shared {@link discussContentItem} (contentRowActions.js carries
   * the full per-type routing rationale — session/note/thread/guide/practice,
   * incl. why the note branch is awaited rather than fire-and-forget). This
   * wrapper exists only for the find-by-key step and the note-branch
   * re-render, since the sidebar's in-memory `items` cache and its own
   * `_render()` aren't visible outside the controller.
   * @param {string} menuKey - "type:id" (see `_itemKey`)
   * @returns {Promise<void>}
   */
  async _discussFromMenuKey(menuKey) {
    const { type, id } = SidebarRecentsController._parseItemKey(menuKey);
    const item = this.items.find(it => it.type === type && String(it.id) === String(id));
    if (!item) return;
    if (await discussContentItem(item)) this._render();
  }

  /**
   * Toggle the options menu on one row (closing any other). A document-level
   * click listener closes it on outside clicks, and hover-out closes it too
   * (mirrors the shared RowMenuController's `hoverClose` behavior).
   * The list always sits inside an internally-scrolled box — the mount itself
   * on desktop, its `.sidebar__scroll` ancestor on mobile (sidebar.css) — so a
   * plain `position: absolute` menu on a row near the
   * bottom of the list gets clipped by that overflow — the menu is instead
   * reparented to `<body>` and switched to viewport-fixed coordinates
   * (`_positionMenu`), mirroring ProfilePopup's portal technique.
   *
   * The portal is why hover-out can't just be `row.addEventListener('mouseleave', close)`
   * the way PartnerNotesSection does it: there, the menu is a DOM child of
   * the row, so leaving the row necessarily means leaving the menu too. Here
   * the menu lives under `<body>`, so the listener has to live on BOTH the
   * row and the menu, and the handler (`_handleMenuAreaMouseLeave`) checks
   * `relatedTarget` against both before closing — otherwise moving the
   * cursor from the row down onto the (visually adjacent but DOM-detached)
   * menu would incorrectly close it.
   *
   * Same story for clicks: once the menu is a `<body>` child, clicks on its
   * Discuss/Archive/Delete items no longer bubble through `this.container`,
   * so the container's delegated `_boundHandleClick` would never see them.
   * Attach it directly to the menu too (removed again in `_closeRowMenu`) —
   * `_handleClick`'s `actionTarget` containment check already accepts
   * `this._menuEl` alongside `this.container` to match.
   * @param {HTMLElement} button - The clicked kebab button
   */
  _toggleRowMenu(button) {
    const key = button.getAttribute('data-menu-key');
    const wasOpen = this._openMenuKey === key;
    this._closeRowMenu();
    if (wasOpen || !key) return;
    const row = button.closest('.sidebar-recent-row');
    const homeParent = button.parentElement; // .note-row__actions
    const menu = homeParent?.querySelector('.options-dropdown__menu');
    if (!row || !menu || !homeParent) return;
    this._openMenuKey = key;
    this._menuEl = menu;
    this._menuHomeParent = homeParent;
    this._menuRowEl = row;
    row.classList.add('sidebar-recent-row--menu-open');
    button.setAttribute('aria-expanded', 'true');
    menu.classList.remove('options-dropdown__menu--hidden');
    document.body.appendChild(menu);
    this._positionMenu(button, menu);
    document.addEventListener('click', this._boundCloseRowMenu);
    menu.addEventListener('click', this._boundHandleClick);
    row.addEventListener('mouseleave', this._boundMenuAreaMouseLeave);
    menu.addEventListener('mouseleave', this._boundMenuAreaMouseLeave);
    // A scroll would leave the portal-positioned menu floating over the wrong
    // row — simplest correct behavior is to close it, matching common dropdown
    // UX, rather than tracking the anchor's position continuously.
    //
    // WHICH element scrolls is viewport-dependent: the mount itself on desktop,
    // its `.sidebar__scroll` ancestor below 1020px (see the mobile fold in
    // sidebar.css). Scroll events do NOT bubble, so listening only on the mount
    // would silently stop closing the menu on mobile. Bind both — each is
    // `{once: true}` and each merely closes the menu, so whichever fires first
    // wins and the other going unused costs nothing.
    SidebarRecentsController._scrollParents(this.container).forEach(el => {
      el.addEventListener('scroll', this._boundCloseRowMenu, { once: true });
    });
    window.addEventListener('resize', this._boundCloseRowMenu, { once: true });
  }

  /**
   * Hover-out close for the portal-reparented menu — bound to `mouseleave`
   * on BOTH the row and the menu (see `_toggleRowMenu`'s doc). Only closes
   * once the cursor has left both; moving directly between them (in either
   * direction) is not a real "leave".
   * @param {MouseEvent} e
   */
  _handleMenuAreaMouseLeave(e) {
    const goingTo = e.relatedTarget;
    if (goingTo && (this._menuRowEl?.contains(goingTo) || this._menuEl?.contains(goingTo))) {
      return;
    }
    this._closeRowMenu();
  }

  /**
   * Position a portal-reparented menu using viewport-fixed coordinates
   * computed from its trigger button — mirrors `ProfilePopup.position()`.
   * Opens below-right of the button by default; flips above if there's no
   * room below, and clamps horizontally to the viewport.
   * @param {HTMLElement} button - The kebab button (anchor)
   * @param {HTMLElement} menu - The `.options-dropdown__menu` element
   */
  _positionMenu(button, menu) {
    const margin = 4;
    const edgeGap = 8;
    const anchorRect = button.getBoundingClientRect();
    const menuRect = menu.getBoundingClientRect();

    let top = anchorRect.bottom + margin;
    if (top + menuRect.height > window.innerHeight - edgeGap) {
      top = anchorRect.top - menuRect.height - margin;
    }
    // Clamp BOTH edges, not just the top. Flipping above only helps when there
    // is room above; for a menu that is tall relative to the viewport — which
    // the mobile menu step-up makes more likely — the flipped position can still
    // overhang the bottom. Order matters: the top clamp runs last so a menu
    // taller than the viewport is pinned to the top edge rather than the bottom.
    top = Math.min(top, window.innerHeight - menuRect.height - edgeGap);
    top = Math.max(edgeGap, top);

    let left = anchorRect.right - menuRect.width;
    left = Math.min(left, window.innerWidth - menuRect.width - edgeGap);
    left = Math.max(left, edgeGap);

    menu.style.position = 'fixed';
    menu.style.top = `${top}px`;
    menu.style.left = `${left}px`;
    menu.style.right = 'auto';
    menu.style.zIndex = '1000';
  }

  /** Close the open row options menu (if any), returning a portal-reparented
   * menu element back to its original row. Safe to call unconditionally. */
  _closeRowMenu() {
    document.removeEventListener('click', this._boundCloseRowMenu);
    this.container?.removeEventListener('scroll', this._boundCloseRowMenu);
    window.removeEventListener('resize', this._boundCloseRowMenu);
    this._menuRowEl?.removeEventListener('mouseleave', this._boundMenuAreaMouseLeave);
    this._menuEl?.removeEventListener('mouseleave', this._boundMenuAreaMouseLeave);
    this._menuEl?.removeEventListener('click', this._boundHandleClick);
    if (!this._openMenuKey) return;
    this._openMenuKey = null;
    const row = this.container?.querySelector('.sidebar-recent-row--menu-open');
    if (row) {
      row.classList.remove('sidebar-recent-row--menu-open');
      row.querySelector('.note-row__options')?.setAttribute('aria-expanded', 'false');
    }
    if (this._menuEl) {
      this._menuEl.classList.add('options-dropdown__menu--hidden');
      this._menuEl.style.position = '';
      this._menuEl.style.top = '';
      this._menuEl.style.left = '';
      this._menuEl.style.right = '';
      this._menuEl.style.zIndex = '';
      this._menuHomeParent?.appendChild(this._menuEl);
    }
    this._menuEl = null;
    this._menuHomeParent = null;
    this._menuRowEl = null;
  }

  /**
   * Run a row action (star | archive | delete), guarded by a single
   * in-flight flag (mirrors SessionsSection._runBulk) so a rapid second
   * click before the first call resolves can't double-fire. Delete confirms
   * first; star/archive don't. After the confirm await, the item's presence
   * is re-checked — a live-refresh event could have already replaced the
   * in-memory list / re-rendered the DOM while the modal was open.
   * @param {'star'|'archive'|'delete'|'view'|'flag'} action
   * @param {string} menuKey - "section:type:id"
   * @returns {Promise<void>}
   */
  async _runRowAction(action, menuKey) {
    if (this._actionInFlight) return;
    const { type, id } = SidebarRecentsController._parseItemKey(menuKey);
    const item = this.items.find(it => it.type === type && String(it.id) === String(id));
    if (!item) return;

    this._actionInFlight = true;
    try {
      if (action === 'delete') {
        const confirmed = await confirmDeleteContentItem(item);
        if (!confirmed) return;
        const stillPresent = this.items.some(it => it.type === type && String(it.id) === String(id));
        if (!stillPresent) return;
        // Runner emits content:deleted on success; a shared runner has no
        // access to THIS controller's own `items` array (each surface owns a
        // differently-shaped one), so removal stays here.
        if (await runDelete(item)) {
          this.items = this.items.filter(it => !(it.type === type && String(it.id) === String(id)));
        }
      } else if (action === 'star') {
        await runStar(item);
      } else if (action === 'archive') {
        await runArchive(item);
      } else if (action === 'view') {
        await runMarkViewed(item, !item.viewed);
      } else if (action === 'flag') {
        await runToggleFlag(item, !item.flagged);
      }
    } finally {
      this._actionInFlight = false;
    }
    this._render();
  }

  /**
   * `content:starred` bridge listener (contentSync). Handles every item type
   * the sidebar holds; idempotent-skips if the
   * item is absent (beyond the loaded set) or already in the target state — so
   * this controller's own emit is a free no-op.
   * @param {{itemType: string, itemId: string, starred: boolean}} data
   */
  _onContentStarred({ itemType, itemId, starred } = {}) {
    const result = reduceNormalizedItems(
      this.items,
      CONTENT_STARRED,
      { itemType, itemId, starred }
    );
    if (!result.changed) return;
    this.items = result.collection;
    this._render();
  }

  /**
   * `content:archived` bridge listener (contentSync). For threads the toggle
   * lives on `payload.dismissedAt` (the system quiet-expiry `archivedAt` is a
   * distinct field — see `_navigateToItem` — and must not be touched); other
   * types use `payload.archivedAt`. Practices instead mirror the lifecycle
   * onto `payload.status`. Idempotent-skips absent/already-matching.
   * @param {{itemType: string, itemId: string, archived: boolean}} data
   */
  _onContentArchived({ itemType, itemId, archived } = {}) {
    const result = reduceNormalizedItems(
      this.items,
      CONTENT_ARCHIVED,
      { itemType, itemId, archived }
    );
    if (!result.changed) return;
    this.items = result.collection;
    this._render();
  }

  /**
   * `content:deleted` bridge listener (contentSync). Removes the item from the
   * in-memory list if present; a no-op (no re-render) if absent.
   * @param {{itemType: string, itemId: string}} data
   */
  _onContentDeleted({ itemType, itemId } = {}) {
    const result = reduceNormalizedItems(
      this.items,
      CONTENT_DELETED,
      { itemType, itemId }
    );
    if (!result.changed) return;
    this.items = result.collection;
    this._render();
  }

  /**
   * `content:viewed` bridge listener (contentSync): keeps this controller's
   * Recents dot in sync with viewed-state changes from every surface, including
   * its own confirmed toggles. Idempotent-skips if the item is absent (beyond
   * the loaded set) or already in the target state.
   * @param {{itemType: string, itemId: string, viewed: boolean}} data
   */
  _onContentViewed({ itemType, itemId, viewed } = {}) {
    const result = reduceNormalizedItems(
      this.items,
      CONTENT_VIEWED,
      { itemType, itemId, viewed }
    );
    if (!result.changed) return;
    this.items = result.collection;
    this._render();
  }

  /**
   * `content:flagged` bridge listener (contentSync), session-only: keeps this
   * controller's own session-row indicators in sync with flag-state
   * changes from every surface, including its own confirmed toggle.
   * Idempotent-skips if the item is absent (beyond the loaded set) or already
   * in the target state.
   * @param {{itemType: string, itemId: string, flagged: boolean}} data
   */
  _onContentFlagged({ itemType, itemId, flagged } = {}) {
    const result = reduceNormalizedItems(
      this.items,
      CONTENT_FLAGGED,
      { itemType, itemId, flagged }
    );
    if (!result.changed) return;
    this.items = result.collection;
    this._render();
  }

  /** Drop partner notes from the in-memory list on couple-unlink and re-render. */
  _onPartnerUnlinked() {
    const hadNotes = this.items.some(it => it.type === 'note');
    if (!hadNotes) return;
    this.items = this.items.filter(it => it.type !== 'note');
    this._render();
  }
}
